import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import ModuleHeader, { ModuleHeaderAction, ModuleHeaderBack } from "../../components/ModuleHeader/ModuleHeader";
import {
  createAdminUser,
  getAdminUser,
  getAdminUserMeta,
  updateAdminUser,
} from "../../api/adminUserApi";
import { getStoredAdmin, isSuperAdmin, updateStoredAdmin } from "../../api/adminAuthApi";
import "./administrator-form.scss";

const permissionDetails = {
  orders: ["Orders", "View and manage guest checkout orders."],
  merchants: ["Merchants", "Manage merchant, outlet, and product setup."],
  administrators: ["Administrators", "Create and edit administrator accounts."],
  settings: ["Settings", "Update platform, payment, and email settings."],
  reports: ["Reports", "View and export sales reports."],
};

const ALL_PERMISSIONS = Object.keys(permissionDetails);

const blankAdmin = {
  name: "",
  email: "",
  mobile: "",
  jobTitle: "Administrator",
  department: "",
  roleId: "",
  status: "1",
  lastLogin: null,
  createdAt: null,
  password: "",
  confirmPassword: "",
  permissions: ["orders", "merchants", "reports"],
};

const formatDateTime = (value, fallback) => {
  if (!value) return fallback;

  return new Intl.DateTimeFormat("en-MY", {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  }).format(new Date(value));
};

const normalizeRoleName = (role) =>
  String(role?.name || role?.display_name || "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "");

const getRoleNameById = (roles, roleId) => {
  const role = roles.find((item) => String(item.id) === String(roleId));
  return normalizeRoleName(role);
};

const getCurrentAdminRoleName = (admin = getStoredAdmin(), roles = []) => {
  const fromRole =
    normalizeRoleName(admin?.role) ||
    normalizeRoleName({ name: admin?.role_name }) ||
    getRoleNameById(roles, admin?.role?.id || admin?.role_id);

  if (fromRole) return fromRole;
  // Legacy session fallback: empty permissions array means full superadmin access.
  if (isSuperAdmin(admin)) return "superadmin";
  return "";
};

const mapAdminToForm = (admin, roles = []) => {
  const roleId = admin.role?.id ? String(admin.role.id) : "";
  const roleName = normalizeRoleName(admin.role) || getRoleNameById(roles, roleId);
  const permissions = roleName === "superadmin"
    ? [...ALL_PERMISSIONS]
    : (admin.permissions || []);

  return {
    name: admin.name || "",
    email: admin.email || "",
    mobile: admin.phone || "",
    jobTitle: admin.job_title || "",
    department: admin.department || "",
    roleId,
    status: String(admin.status ?? 1),
    lastLogin: admin.last_logged_in,
    createdAt: admin.created_at,
    password: "",
    confirmPassword: "",
    permissions,
  };
};

const AdministratorFormPage = ({ mode = "edit" }) => {
  const navigate = useNavigate();
  const { adminId } = useParams();
  const isCreate = mode === "create";
  const currentAdmin = getStoredAdmin();

  const [form, setForm] = useState(blankAdmin);
  const [roles, setRoles] = useState([]);
  const [currentRoleName, setCurrentRoleName] = useState(() => getCurrentAdminRoleName());
  const [savedRoleName, setSavedRoleName] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [notice, setNotice] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  const isCurrentManagement = currentRoleName === "management";
  const isCurrentSuperadmin = currentRoleName === "superadmin";

  const targetRoleName = useMemo(
    () => getRoleNameById(roles, form.roleId),
    [roles, form.roleId]
  );
  const isTargetSuperadmin = targetRoleName === "superadmin";
  const isEditingSelf = !isCreate && currentAdmin && String(currentAdmin.id) === String(adminId);
  const wasSavedAsManagement = isCreate || savedRoleName === "management";

  // Role stays editable based on the last saved role (not the unsaved select value),
  // so switching Management → Superadmin before save does not lock the dropdown.
  // Superadmin can edit role for saved Management users only.
  // Superadmin / Management can edit module permissions for other Management users.
  const canEditRole = isCreate || (isCurrentSuperadmin && wasSavedAsManagement && !isEditingSelf);
  const canEditPermissions = !isTargetSuperadmin && (
    isCreate ||
    (wasSavedAsManagement && (
      isCurrentSuperadmin ||
      (isCurrentManagement && !isEditingSelf)
    ))
  );
  const canEditStatus = !isEditingSelf;

  useEffect(() => {
    let active = true;

    const load = async () => {
      setIsLoading(true);
      setErrorMessage("");

      try {
        const [meta, admin, actorProfile] = await Promise.all([
          getAdminUserMeta(),
          isCreate ? Promise.resolve(null) : getAdminUser(adminId),
          currentAdmin?.id ? getAdminUser(currentAdmin.id).catch(() => null) : Promise.resolve(null),
        ]);

        if (!active) return;

        const nextRoles = meta.roles || [];
        setRoles(nextRoles);

        if (actorProfile?.role) {
          updateStoredAdmin({ role: actorProfile.role });
        }

        const actorRole =
          normalizeRoleName(actorProfile?.role) ||
          getRoleNameById(nextRoles, actorProfile?.role?.id) ||
          getCurrentAdminRoleName(
            actorProfile ? { ...currentAdmin, ...actorProfile } : currentAdmin,
            nextRoles
          ) ||
          "management";
        setCurrentRoleName(actorRole);

        if (admin) {
          const mapped = mapAdminToForm(admin, nextRoles);
          if (!mapped.roleId && nextRoles[0]?.id) {
            mapped.roleId = String(nextRoles[0].id);
          }
          setSavedRoleName(
            normalizeRoleName(admin.role) || getRoleNameById(nextRoles, mapped.roleId)
          );
          setForm(mapped);
        } else {
          const defaultRoleId = nextRoles[0]?.id ? String(nextRoles[0].id) : "";
          const defaultRoleName = getRoleNameById(nextRoles, defaultRoleId);
          setSavedRoleName("");
          setForm({
            ...blankAdmin,
            roleId: defaultRoleId,
            permissions: defaultRoleName === "superadmin"
              ? [...ALL_PERMISSIONS]
              : blankAdmin.permissions,
          });
        }
      } catch (error) {
        if (active) setErrorMessage(error.message);
      } finally {
        if (active) setIsLoading(false);
      }
    };

    load();
    return () => {
      active = false;
    };
  }, [adminId, isCreate, currentAdmin?.id]);

  const updateForm = (field, value) => {
    setNotice("");
    setErrorMessage("");
    setForm((current) => {
      if (field === "roleId") {
        const nextRoleName = getRoleNameById(roles, value);
        return {
          ...current,
          roleId: value,
          permissions: nextRoleName === "superadmin"
            ? [...ALL_PERMISSIONS]
            : current.permissions,
        };
      }

      return { ...current, [field]: value };
    });
  };

  const togglePermission = (permission) => {
    if (!canEditPermissions) return;

    updateForm(
      "permissions",
      form.permissions.includes(permission)
        ? form.permissions.filter((item) => item !== permission)
        : [...form.permissions, permission]
    );
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    const showValidationError = (message, fieldName) => {
      setErrorMessage(message);
      window.requestAnimationFrame(() => {
        const field = document.querySelector(`[name="${fieldName}"]`);
        field?.focus();
        field?.scrollIntoView({ behavior: "smooth", block: "center" });
      });
    };

    if (!form.name.trim() || !form.email.trim()) {
      showValidationError(
        "Full name and email address are required.",
        !form.name.trim() ? "name" : "email"
      );
      return;
    }

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) {
      showValidationError("Please enter a valid email address.", "email");
      return;
    }

    if (isCreate && !form.password) {
      showValidationError("A temporary password is required.", "password");
      return;
    }

    if (form.password && form.password.length < 8) {
      showValidationError("Password must be at least 8 characters.", "password");
      return;
    }

    if (form.password !== form.confirmPassword) {
      showValidationError("Password confirmation does not match.", "password_confirmation");
      return;
    }

    if (!form.roleId) {
      showValidationError("Please select a role.", "role");
      return;
    }

    const submitRoleName = getRoleNameById(roles, form.roleId);
    const permissions = submitRoleName === "superadmin"
      ? [...ALL_PERMISSIONS]
      : form.permissions;

    const payload = {
      name: form.name.trim(),
      email: form.email.trim(),
      phone: form.mobile || null,
      job_title: form.jobTitle || null,
      department: form.department || null,
      locale: "en",
      status: Number(form.status),
      role_id: Number(form.roleId),
      permissions,
      ...(form.password ? {
        password: form.password,
        password_confirmation: form.confirmPassword,
      } : {}),
    };

    setIsSaving(true);
    setErrorMessage("");
    setNotice("");

    try {
      const savedAdmin = isCreate
        ? await createAdminUser(payload)
        : await updateAdminUser(adminId, payload);

      setForm(mapAdminToForm(savedAdmin, roles));
      setSavedRoleName(
        normalizeRoleName(savedAdmin.role) || getRoleNameById(roles, savedAdmin.role?.id)
      );
      setNotice(isCreate
        ? "Administrator created successfully."
        : "Administrator updated successfully.");

      if (currentAdmin && String(currentAdmin.id) === String(savedAdmin.id)) {
        updateStoredAdmin(savedAdmin);
      }

      if (isCreate) {
        navigate(`/administrators/${savedAdmin.id}/edit`, { replace: true });
      }
    } catch (error) {
      setErrorMessage(error.message);
    } finally {
      setIsSaving(false);
    }
  };

  const renderPermission = (key) => {
    const [label, description] = permissionDetails[key];
    const checked = isTargetSuperadmin || form.permissions.includes(key);

    return (
      <label
        className={`administrator-form__switch${!canEditPermissions ? " is-disabled" : ""}`}
        key={key}
      >
        <input
          type="checkbox"
          checked={checked}
          onChange={() => togglePermission(key)}
          disabled={isLoading || isSaving || !canEditPermissions}
        />
        <span>
          <strong>{label}</strong>
          <small>{description}</small>
        </span>
      </label>
    );
  };

  const permissionsHint = isTargetSuperadmin
    ? "Superadmin always has access to all modules."
    : isCreate
      ? "Assign module access for the new administrator."
      : isCurrentSuperadmin && wasSavedAsManagement
        ? "You can adjust role and module access for this management administrator."
        : isCurrentManagement && wasSavedAsManagement && !isEditingSelf
          ? "You can adjust module access for this management administrator."
          : isEditingSelf
            ? "You cannot change your own module permissions."
            : isCurrentManagement
              ? "Management users cannot change role for administrators."
              : "Direct module access assigned to this administrator.";

  return (
    <form className="administrator-form" onSubmit={handleSubmit} noValidate>
      <ModuleHeader
        title={isCreate ? "Add Administrator" : "Edit Administrator"}
        description={
          isCreate
            ? "Create a new administrator account and assign access."
            : "Update administrator account details, role, status, and permissions."
        }
        back={(
          <ModuleHeaderBack type="button" onClick={() => navigate("/administrators")}>
            ← Back to Administrators
          </ModuleHeaderBack>
        )}
        actions={(
          <ModuleHeaderAction type="submit" disabled={isLoading || isSaving}>
            {isSaving ? "Saving..." : isCreate ? "Create Administrator" : "Save Administrator"}
          </ModuleHeaderAction>
        )}
      />

      {(notice || errorMessage) && (
        <div className={`administrator-form__notice ${errorMessage ? "administrator-form__notice--error" : ""}`} role={errorMessage ? "alert" : "status"}>
          <div className="administrator-form__container">{errorMessage || notice}</div>
        </div>
      )}

      <section className="administrator-form__summary-wrap">
        <div className="administrator-form__container administrator-form__summary">
          <div className="administrator-form__avatar">{form.name ? form.name.charAt(0).toUpperCase() : "A"}</div>
          <div>
            <h3>{form.name || "New Administrator"}</h3>
            <p>{form.email || "Email not set"}</p>
          </div>
          <span className={`administrator-form__badge administrator-form__badge--${form.status === "1" ? "active" : "inactive"}`}>
            {form.status === "1" ? "Active" : "Inactive"}
          </span>
        </div>
      </section>

      <div className="administrator-form__container administrator-form__grid">
        <section className="administrator-form__panel administrator-form__panel--wide">
          <div className="administrator-form__panel-title">
            <h3>Account Information</h3>
            <p>Administrator identity and platform access details.</p>
          </div>

          <div className="administrator-form__form-grid">
            <label>
              <span>Full Name *</span>
              <input name="name" value={form.name} onChange={(event) => updateForm("name", event.target.value)} placeholder="Enter full name" disabled={isLoading || isSaving} />
            </label>
            <label>
              <span>Email Address *</span>
              <input name="email" type="email" value={form.email} onChange={(event) => updateForm("email", event.target.value)} placeholder="name@secondbite.my" disabled={isLoading || isSaving} />
            </label>
            <label className="administrator-form__phone-field">
              <span>Mobile Number</span>
              <PhoneInput
                country="my"
                value={form.mobile}
                onChange={(value) => updateForm("mobile", value)}
                preferredCountries={["my", "sg", "bn", "id"]}
                enableSearch
                countryCodeEditable={false}
                disableSearchIcon
                disabled={isLoading || isSaving}
                inputProps={{ name: "mobile", autoComplete: "tel" }}
              />
            </label>
            <label>
              <span>Job Title</span>
              <input value={form.jobTitle} onChange={(event) => updateForm("jobTitle", event.target.value)} placeholder="Administrator" disabled={isLoading || isSaving} />
            </label>
            <label>
              <span>Department</span>
              <input value={form.department} onChange={(event) => updateForm("department", event.target.value)} placeholder="Operations" disabled={isLoading || isSaving} />
            </label>
            <label>
              <span>Role *</span>
              <select
                name="role"
                value={form.roleId}
                onChange={(event) => updateForm("roleId", event.target.value)}
                disabled={isLoading || isSaving || !canEditRole}
                required
              >
                {roles.map((role) => (
                  <option value={role.id} key={role.id}>{role.display_name || role.name}</option>
                ))}
              </select>
            </label>
            <label>
              <span>Status</span>
              <select
                value={form.status}
                onChange={(event) => updateForm("status", event.target.value)}
                disabled={isLoading || isSaving || !canEditStatus}
              >
                <option value="1">Active</option>
                <option value="0">Inactive</option>
              </select>
            </label>
          </div>
        </section>

        <section className={`administrator-form__panel${!canEditPermissions ? " is-disabled" : ""}`}>
          <div className="administrator-form__panel-title">
            <h3>Module Permissions</h3>
            <p>{permissionsHint}</p>
          </div>
          <div className="administrator-form__switch-list">
            {ALL_PERMISSIONS.map(renderPermission)}
          </div>
        </section>

        <section className="administrator-form__panel">
          <div className="administrator-form__panel-title">
            <h3>Access Summary</h3>
            <p>Current account status.</p>
          </div>
          <div className="administrator-form__status-list">
            <div><span>Last Login</span><strong>{formatDateTime(form.lastLogin, "Never logged in")}</strong></div>
            <div><span>Created Date</span><strong>{formatDateTime(form.createdAt, isCreate ? "Not created" : "—")}</strong></div>
            <div><span>Role</span><strong>{roles.find((role) => String(role.id) === form.roleId)?.display_name || "—"}</strong></div>
          </div>
        </section>

        <section className="administrator-form__panel administrator-form__panel--wide">
          <div className="administrator-form__panel-title">
            <h3>{isCreate ? "Temporary Password" : "Reset Password"}</h3>
            <p>{isCreate ? "Set the first login password." : "Leave both fields blank to keep the current password."}</p>
          </div>
          <div className="administrator-form__form-grid">
            <label>
              <span>{isCreate ? "Temporary Password *" : "New Password"}</span>
              <input name="password" type="password" value={form.password} onChange={(event) => updateForm("password", event.target.value)} placeholder="Minimum 8 characters" disabled={isLoading || isSaving} />
            </label>
            <label>
              <span>Confirm Password</span>
              <input name="password_confirmation" type="password" value={form.confirmPassword} onChange={(event) => updateForm("confirmPassword", event.target.value)} placeholder="Confirm password" disabled={isLoading || isSaving} />
            </label>
          </div>
        </section>
      </div>

      <div className="administrator-form__footer-wrap">
        <div className="administrator-form__container administrator-form__footer">
          {errorMessage ? (
            <span className="administrator-form__footer-error" role="alert">
              {errorMessage}
            </span>
          ) : null}
          <button type="button" className="administrator-form__secondary" onClick={() => navigate("/administrators")}>Cancel</button>
          <button type="submit" className="administrator-form__primary" disabled={isLoading || isSaving}>
            {isSaving ? "Saving..." : isCreate ? "Create Administrator" : "Save Administrator"}
          </button>
        </div>
      </div>
    </form>
  );
};

export default AdministratorFormPage;
